import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import NewVirtualKeyboard from 'components/NewVirtualKeyboard/NewVirtualKeyboard';
import { BetFocusFields } from 'constants/betslip';
import useOddsType from 'hooks/useOddsType';
import { getIsLandscape } from 'redux/modules/appConfigs/selectors';
import { BetFocusField } from 'redux/modules/betslip/type';
import { TInlineSelectedBet } from 'redux/modules/inlinePlacement/type';
import { TPrice, TSize } from 'types/bets';
import { calculateSize } from 'utils/liability';
import convertOdds from 'utils/oddsConverter';

import styles from './PlacementVirtualKeyboard.module.scss';

const VALIDATION_REGEX = /^\d+\.{0,1}\d{0,2}$/;
const AMERICAN_VALIDATION_REGEX = /^[-+]?[1-9]\d*$/;

interface PlacementVirtualKeyboardProps {
  isDisabledKeys: boolean;
  currentFocus: BetFocusField | null;
  priceValue: TPrice;
  sizeValue: TSize;
  customPriceValue: string | null;
  liabilityValue: string | number | undefined;
  bet: TInlineSelectedBet;
  isLineMarket: boolean;
  setLiabilityValue: (value: string | number | undefined) => void;
  setPriceValue: (price: TPrice) => void;
  setSizeValue: (price: TSize) => void;
  handleDirtySize: () => void;
  setCustomPriceValue: (price: string | null) => void;
}

const PlacementVirtualKeyboard = ({
  isDisabledKeys,
  currentFocus,
  priceValue,
  sizeValue,
  customPriceValue,
  liabilityValue,
  bet,
  isLineMarket,
  setLiabilityValue,
  setPriceValue,
  setSizeValue,
  handleDirtySize,
  setCustomPriceValue
}: PlacementVirtualKeyboardProps) => {
  const isLandscape = useSelector(getIsLandscape);
  const { exchangeOddsType, isAmericanOdds } = useOddsType();
  const isUSOdds = isAmericanOdds && !isLineMarket;
  const currentPriceValue = isUSOdds
    ? customPriceValue !== null
      ? customPriceValue
      : convertOdds(priceValue || '', exchangeOddsType)
    : priceValue;

  useEffect(() => {
    if (customPriceValue !== null || !isAmericanOdds) {
      setCustomPriceValue(null);
    }
  }, [priceValue, isAmericanOdds]);

  const setPrice = (key: string) => {
    const priceRegex = isUSOdds ? AMERICAN_VALIDATION_REGEX : VALIDATION_REGEX;

    if (!isLineMarket && (key === '-' || key === '+') && (isUSOdds ? currentPriceValue === '' : !priceValue)) {
      if (isUSOdds) {
        setCustomPriceValue(key);
      } else {
        setPriceValue(key);
      }

      return;
    }

    if (priceValue === '0' && key !== '.') return;

    const newPriceValue =
      priceValue || customPriceValue ? `${isUSOdds ? customPriceValue || currentPriceValue : priceValue}${key}` : key;

    if (priceRegex.test(newPriceValue)) {
      if (isUSOdds) {
        setCustomPriceValue(newPriceValue);
      } else {
        setPriceValue(newPriceValue);
      }
    }
  };

  const handleKeyClick = (key: string) => {
    if (currentFocus === BetFocusFields.PRICE) {
      setPrice(key);
    } else if (currentFocus === BetFocusFields.SIZE) {
      if (sizeValue === '0' && key !== '.') return;
      let newSizeValue = sizeValue + key;

      if (key === '00' && !sizeValue) {
        newSizeValue = '0';
      }
      if (VALIDATION_REGEX.test(newSizeValue)) {
        setSizeValue(newSizeValue);
        handleDirtySize();
      }
    } else {
      const stringLiabilityValue = liabilityValue === 0 ? '' : liabilityValue;
      if (stringLiabilityValue === '0' && key !== '.') return;
      let newLiabilityValue = stringLiabilityValue + key;

      if (key === '00' && !stringLiabilityValue) {
        newLiabilityValue = '0';
      }

      if (VALIDATION_REGEX.test(newLiabilityValue)) {
        setLiabilityValue(newLiabilityValue);
        const newSizeValue = calculateSize(priceValue, newLiabilityValue, { bettingType: bet.bettingType });
        setSizeValue(newSizeValue);
      }
    }
  };

  const handleBackSpace = () => {
    if (currentFocus === BetFocusFields.PRICE) {
      if (currentPriceValue) {
        const newPriceValue = currentPriceValue.toString().slice(0, -1);

        if (isUSOdds) {
          setCustomPriceValue(newPriceValue);
        } else {
          setPriceValue(newPriceValue);
        }
      }
    } else if (currentFocus === BetFocusFields.SIZE) {
      if (sizeValue) {
        const newSizeValue = sizeValue.toString().slice(0, -1);

        setSizeValue(newSizeValue);
      }
    } else {
      const newLiabilityValue = liabilityValue?.toString().slice(0, -1) ?? '';
      const newSizeValue = calculateSize(priceValue, newLiabilityValue, { bettingType: bet.bettingType });

      setLiabilityValue(newLiabilityValue);
      setSizeValue(newSizeValue);
    }
  };

  return (
    <NewVirtualKeyboard
      onKeyClick={handleKeyClick}
      onBackSpace={handleBackSpace}
      isDisabledKeys={isDisabledKeys}
      containerClassName={classNames(styles.keyboard, { [styles.keyboard__landscape]: isLandscape })}
      showOperators={!isLineMarket && isAmericanOdds && currentFocus === BetFocusFields.PRICE}
      shouldIgnoreBlurEvent
    />
  );
};

export default PlacementVirtualKeyboard;
